import { AxiosRequestConfig, CancelTokenSource, default as axios2 } from 'axios'
import _ from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { getCountryCode, getLocaleMap } from '../redux/config'
import { LogActions } from '../redux/logs'
import { RootState } from '../redux/types'
import { createRequestParams } from '../utils/api'
import { mapActionError } from '../utils/helpers'

import { useAppConfig } from './useAppConfig'

export function useApiData<T = any>(
  params: AxiosRequestConfig,
  config: {
    umbracoCall?: boolean
    skip?: boolean
    overrideCountryCode?: string
    skipCountryCode?: boolean
  } = {}
) {
  const { umbracoCall, skip, overrideCountryCode, skipCountryCode } = config
  const { axios } = useAppConfig()
  const { i18n } = useTranslation()
  const [data, setData] = useState<T>()
  const [errorStatus, setErrorStatus] = useState<number | 'unknown' | undefined>()
  const [loading, setLoading] = useState<boolean>(skip ? false : true)
  const [refreshFlag, setRefreshFlag] = useState<boolean>(false)
  const localeMap = useSelector((state: RootState) => getLocaleMap(state, i18n.language))
  const countryCode = useSelector(getCountryCode)
  const cancelRef = useRef<CancelTokenSource | null>(null)
  const dispatch = useDispatch()
  const countryCodeToApply = skipCountryCode
    ? undefined
    : _.isString(overrideCountryCode)
      ? overrideCountryCode
      : countryCode

  useEffect(() => {
    if (refreshFlag) {
      setRefreshFlag(false)
      return
    }

    if (skip) {
      setData(undefined)
      return
    }

    setErrorStatus(undefined)

    if (cancelRef.current) {
      cancelRef.current.cancel()
    }
    cancelRef.current = axios2.CancelToken.source()

    setLoading(true)

    const requestConfig = createRequestParams(params, localeMap, {
      umbracoCall
    })

    const config: AxiosRequestConfig = {
      ...requestConfig,
      params: {
        ...params.params,
        countryCode: countryCodeToApply
      }
    }

    axios(config)
      .then(res => {
        setData(res.data)
      })
      // eslint-disable-next-line no-console
      .catch(e => {
        if (!axios2.isCancel(e)) {
          if (_.isNumber(e.response?.status)) {
            setErrorStatus(_.toNumber(e.response.status))
          } else {
            setErrorStatus('unknown')
          }
          dispatch(LogActions.addLogItem(mapActionError(e, 'ERROR', location.pathname)))
        }
      })
      .finally(() => {
        setLoading(false)
      })
    return () => cancelRef.current?.cancel()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(params.params),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(params.data),
    skip,
    refreshFlag,
    countryCodeToApply
  ])

  return {
    data: data,
    loading: loading,
    error: !!errorStatus,
    errorStatus,
    refresh: () => setRefreshFlag(true)
  }
}
